import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { closeDialog } from '@/features/dialog/dialogSlice';
import { useFormikContext } from 'formik';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import getCorrectFolderOwner from '@/hooks/getCorrectFolderOwner';
import { SharepointFileSelectDialog } from '@/components/dialogs/SharepointFileSelectDialog';
import { getUniqueFileName, normalizeAllowedFileTypes } from '@/features/msGraph/helper';

export const AddFileDialog = () => {
	const dispatch = useDispatch();
	const { values, setFieldValue } = useFormikContext();
	const isOpen = useSelector((state) => state.dialog.open.selectFiles);
	const dialogData = useSelector((state) => state.dialog.data.selectFiles);

	const multiple = dialogData?.multiple ?? true;
	const allowedTypes = useMemo(
		() => normalizeAllowedFileTypes(dialogData?.types),
		[dialogData?.types],
	);

	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;

	const { data: userDetails } = useGetUserDetailsQuery({ email }, { skip: !email });

	const { correctOwner: folderOwnerId } = getCorrectFolderOwner({
		deal: values.deal,
		client: values.client,
	});

	const { data: folderOwner } = useGetUserDetailsQuery(
		{ userId: folderOwnerId },
		{ skip: !folderOwnerId },
	);

	const itemId = values.deal?.driveId ?? values.client?.driveId ?? 'root';
	const driveId =
		values.deal?.clientFolderDriveId ??
		values.client?.clientFolderDriveId ??
		folderOwner?.clientFolderDriveId ??
		userDetails?.clientFolderDriveId;

	useEffect(() => {
		if (setFieldValue && driveId) {
			setFieldValue('driveId', driveId);
		}
	}, [driveId, setFieldValue]);

	const handleClose = () => {
		dispatch(closeDialog('selectFiles'));
	};

	const handleConfirm = (selectedFiles) => {
		const sharePointFiles = selectedFiles.map((file) => ({
			id: file.id,
			name: getUniqueFileName(values.files || [], file.name),
			size: file.size,
			webUrl: file.webUrl,
			mimeType: file.mimeType,
			originalName: file.name,
			driveId: file.driveId,
		}));

		setFieldValue('files', (currentFiles) => {
			const current = currentFiles || [];
			return [...current, ...sharePointFiles];
		});
	};

	return (
		<SharepointFileSelectDialog
			open={isOpen}
			onClose={handleClose}
			onConfirm={handleConfirm}
			itemId={itemId}
			driveId={driveId}
			disabled={!values.deal || itemId === 'root'}
			multiple={multiple}
			types={allowedTypes}
			hideBackdrop
		/>
	);
};
